import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PreviewPlaceholder } from '../../../components/wizard';
import { findThemeScreenshot } from '../../../data/preview';
import { usePreviewSlot } from '../../../lib/staticPreview';
import { useConfigurator } from '../../../store/configurator';
import { PhoneFrame } from '../../../steps/finish/components/DevicePreview';
import { PreviewImg } from './MobileInlinePreview.styles';

/**
 * Inline phone preview for the mobile design flow. Shows a pre-rendered STATIC
 * screenshot of the configured site (resolved per theme / colour / art-style /
 * locale) inside the phone bezel — never a live `<iframe>`.
 *
 * The live casino site is heavy; kept warm inline through the whole design flow it
 * froze RAM-constrained phones (Safari reloads / hard freezes mid-flow). The real
 * live site is one tap away via the "Preview" pill ({@link MobileSitePreview}), which
 * mounts an iframe only WHILE open and destroys it on close. When the backend has no
 * screenshot yet for the current selection, the brand-coloured placeholder shows
 * instead — still no iframe. The image swaps live as the user changes selection.
 */
export const MobileInlinePreview = (): React.ReactElement => {
  const { i18n } = useTranslation();
  const { selectedTheme, selectedColor, selectedStyle } = useConfigurator();

  const lang = (i18n.language || 'en').split('-')[0];
  const screenshot = findThemeScreenshot(selectedTheme, selectedColor, selectedStyle, lang);
  const slot = usePreviewSlot(screenshot?.iphone ?? '');

  // Reveal on the image's own load; a new capture (selection change) re-gates it so the
  // brand placeholder covers the swap instead of flashing the previous shot. Keyed on the
  // src that loaded rather than reset in an effect: the first mount comes from a
  // non-discrete update, so its effects can flush AFTER a fast (cached) load and the reset
  // would hide the shot for good, leaving the spinner up.
  const [loadedSrc, setLoadedSrc] = useState('');
  const shown = slot.src !== '' && loadedSrc === slot.src;

  return (
    <PhoneFrame
      fit="width"
      topColor={selectedColor?.topNav}
      bottomColor={shown ? selectedColor?.bottomNav : selectedColor?.topNav}
      overlay={
        <PreviewPlaceholder shown={!shown} spinner={!slot.exhausted} spinnerAlign="top" bgColor={selectedColor?.bg} />
      }
    >
      {slot.src ? (
        <PreviewImg src={slot.src} alt="" $shown={shown} onLoad={() => setLoadedSrc(slot.src)} onError={slot.onError} />
      ) : null}
    </PhoneFrame>
  );
};

export default MobileInlinePreview;
